import { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  LinearProgress,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { tableCellClasses } from '@mui/material/TableCell';
import InspectionDocument from './InspectionDocument';
import INSPECTION_STATUSES from '@/components/inspectionStatuses';
import RunningInspection from './RunningInspection';
import getScoreCategoryDetails from '../getScoreCategoryDetails';
import getDocumentScore from '../getDocumentScore';
import INSPECTION_TYPES from '@/components/inspectionTypes';
import type { Inspection, EntityId } from '@/types/apiTypes';
import type { Manifest } from '@/types/apiTypes';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps): React.JSX.Element {
  const { children, value, index, ...other } = props;

  return (
    <div
      style={{
        display: 'flex',
        flexGrow: 1, // This ensures the TabPanel grows to fill available space
      }}
      role='tabpanel'
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3, flexGrow: 1 }}>{children}</Box>}
    </div>
  );
}

interface InspectionDocumentsProps {
  inspection: Inspection & {
    status: string;
    type: INSPECTION_TYPES;
    documents?: Array<{
      document_entity_id?: string;
      document_doc_id?: string;
      document_name?: string;
      critical_compliant?: number;
      critical_non_compliant?: number;
      non_critical_compliant?: number;
      non_critical_non_compliant?: number;
      [key: string]: unknown;
    }>;
    [key: string]: unknown;
  };
  manifests: (Manifest & {
    checks?: Array<{
      section?: string;
      name: string;
      entity_id?: EntityId;
      reference?: string;
      [key: string]: unknown;
    }>;
    global?: boolean;
    reference_document_content?: string;
  })[];
}

function InspectionDocuments({
  inspection,
  manifests,
}: InspectionDocumentsProps): React.JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = useState<number>(0);
  const [selected_doc, setSelectedDoc] = useState<{
    document_entity_id?: string;
    document_doc_id?: string;
    document_name?: string;
    [key: string]: unknown;
  } | null>(null);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };
  const handleRowClick = (row: {
    document_entity_id?: string;
    [key: string]: unknown;
  }) => {
    setSelectedDoc(row);
  };

  if (!inspection) {
    return <></>;
  }

  if (inspection?.status === INSPECTION_STATUSES.DRAFT) {
    if (inspection?.type === INSPECTION_TYPES.EFFECTIVE) {
      return (
        <Box padding='15px'>
          <Typography variant='subtitle1'>Documents</Typography>
          <Divider />
          <br />
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'background.paper',
              display: 'flex',
            }}
          >
            <Tabs
              orientation='vertical'
              variant='scrollable'
              value={tab}
              onChange={handleTabChange}
              sx={{
                borderRight: 1,
                borderColor: 'divider',
                maxWidth: '200px',
                minWidth: '150px',
              }}
            >
              {(inspection?.documents || []).map((document, i) => {
                return (
                  <Tab
                    key={document.document_entity_id || i}
                    label={`${document.document_doc_id || ''} - ${document.document_name || ''}`}
                    value={i}
                    sx={{
                      textAlign: 'left',
                      alignItems: 'flex-start',
                      wordBreak: 'break-word',
                      whiteSpace: 'normal',
                    }}
                  />
                );
              })}
            </Tabs>
            {(inspection?.documents || []).map((document, i) => {
              return (
                <TabPanel key={`${i}`} value={tab} index={i}>
                  <InspectionDocument
                    manifests={manifests}
                    inspection={inspection}
                    document={document}
                  />
                </TabPanel>
              );
            })}
          </Box>
        </Box>
      );
    } else {
      return (
        <Box padding='15px'>
          <Box
            sx={{
              flexGrow: 1,
              bgcolor: 'background.paper',
              display: 'flex',
            }}
          >
            <InspectionDocument manifests={manifests} inspection={inspection} />
          </Box>
        </Box>
      );
    }
  }

  if (
    [INSPECTION_STATUSES.RUNNING, INSPECTION_STATUSES.QUEUED].includes(
      inspection?.status as INSPECTION_STATUSES,
    )
  ) {
    return (
      <>
        <Box padding='15px'>
          <Typography variant='subtitle1'>Running Inspection</Typography>
          <LinearProgress />
          <br />
          <RunningInspection inspection={inspection} />
        </Box>
      </>
    );
  }
  if (inspection?.type === INSPECTION_TYPES.EFFECTIVE) {
    return (
      <>
        <Box padding='15px'>
          <Typography variant='subtitle1'>Inspection Results</Typography>
          <Divider />
          <br />
          <TableContainer component={Paper}>
            <Table
              sx={{ minWidth: isMobile ? 400 : 650 }}
              stickyHeader
              size='small'
            >
              <TableHead>
                <TableRow>
                  <StyledTableCell>Document</StyledTableCell>
                  <StyledTableCell
                    sx={{
                      textAlign: 'left',
                    }}
                  >
                    Rating
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    Critical Checks
                  </StyledTableCell>
                  <StyledTableCell align='center'>
                    Non-Critical Checks
                  </StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(inspection.documents || []).map((document) => (
                  <TableRow
                    key={document.document_entity_id}
                    onClick={() => handleRowClick(document)}
                    sx={{
                      cursor: 'pointer',
                      backgroundColor:
                        selected_doc?.document_entity_id ===
                        document.document_entity_id
                          ? 'rgba(0, 0, 255, 0.1)'
                          : 'inherit',
                    }}
                  >
                    <TableCell>
                      <Typography variant='body2'>
                        {document.document_doc_id || ''} -{' '}
                        {document.document_name || ''}
                      </Typography>
                    </TableCell>

                    <TableCell>
                      <Typography variant='body2'>
                        {
                          getScoreCategoryDetails(
                            getDocumentScore(
                              (document?.critical_compliant || 0) +
                                (document?.critical_non_compliant || 0),
                              (document?.non_critical_compliant || 0) +
                                (document?.non_critical_non_compliant || 0),
                              document?.critical_compliant || 0,
                              document?.non_critical_compliant || 0,
                            ),
                          )?.label
                        }
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ textDecoration: 'underline' }}
                      >
                        {document?.critical_compliant || 0} /{' '}
                        {(document?.critical_compliant || 0) +
                          (document?.critical_non_compliant || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: 'right',
                      }}
                    >
                      <Typography
                        variant='body2'
                        sx={{ textDecoration: 'underline' }}
                      >
                        {document?.non_critical_compliant || 0} /{' '}
                        {(document?.non_critical_compliant || 0) +
                          (document?.non_critical_non_compliant || 0)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <br />
          {selected_doc ? (
            <InspectionDocument
              key={selected_doc.document_entity_id}
              inspection={inspection}
              document={selected_doc}
              manifests={manifests}
            />
          ) : undefined}
        </Box>
      </>
    );
  } else {
    return (
      <>
        <Box padding='15px'>
          <InspectionDocument
            key={inspection.entity_id}
            inspection={inspection}
            manifests={manifests}
          />
        </Box>
      </>
    );
  }
}

export default InspectionDocuments;
